import { useCallback } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
  SelectTrigger,
  Text,
  TuneIcon,
  ViewListIcon,
} from '@billda/design-system';
import { useShallow } from 'zustand/react/shallow';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import {
  usePaymentsUIStore,
  type PaymentGroupingMode,
} from '../stores/paymentsUIStore';

/**
 * PaymentGroupingMenu — Linear-style "Display" popover that lets users
 * switch the payment list grouping between "None", "Pot", and "Gang / Solo".
 * Reads and writes `groupingMode` from `paymentsUIStore`.
 */
export function PaymentGroupingMenu() {
  const { groupingMode, setGroupingMode } = usePaymentsUIStore(
    useShallow(state => ({
      groupingMode: state.groupingMode,
      setGroupingMode: state.setGroupingMode,
    }))
  );

  const handleGroupingChange = useCallback(
    (value: string) => {
      setGroupingMode(value as PaymentGroupingMode);
    },
    [setGroupingMode]
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <PageHeaderAction
          label="Display"
          icon={<TuneIcon />}
          data-testid="payment-display-button"
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label="Payment display options"
        className="min-w-70"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ViewListIcon
              aria-hidden="true"
              className="size-4 text-content-secondary"
            />
            <Text variant="body2" color="secondary">
              Grouping
            </Text>
          </div>
          <Select value={groupingMode} onValueChange={handleGroupingChange}>
            <SelectTrigger
              aria-label="Group payments by"
              data-testid="payment-grouping-select"
              className="min-w-35 bg-surface-raised text-[13px]"
            />
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="pot">Pot</SelectItem>
            <SelectItem value="gang">Gang / Solo</SelectItem>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  );
}
